<template>
  <div v-if="isVisible" class="artist-video-section">
    <div class="container">
      <div class="video-card" ref="videoCard">
        <!-- YouTube Thumbnail, visible behind loader and iframe -->
        <img
          :src="currentThumbnailUrl"
          class="video-thumbnail"
          @error="onThumbnailError"
          alt="Youtube Video Thumbnail"
        />

        <!-- Loading overlay — sits in front of iframe, dismissed after video loads -->
        <transition name="fade-loader">
          <div v-if="showLoader" class="video-loader">
            <div class="loader-shimmer"></div>
            <div class="loader-content">
              <div class="loader-pulse-ring"></div>
              <svg class="loader-play-icon" viewBox="0 0 68 68" xmlns="http://www.w3.org/2000/svg">
                <circle cx="34" cy="34" r="32" fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.25)" stroke-width="2"/>
                <polygon points="27,20 27,48 50,34" fill="rgba(255,255,255,0.7)"/>
              </svg>
            </div>
          </div>
        </transition>

        <!-- Direct iframe for reliable autoplay. Injected on intersection. -->
        <iframe
          v-if="shouldLoadVideo"
          :src="iframeSrc"
          :class="{ 'iframe-visible': videoReady }"
          frameborder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowfullscreen
          ref="videoIframe"
          @load="onIframeLoad"
        ></iframe>
      </div>
    </div>
  </div>
</template>

<script>
import { getIpLocation } from '@/utils/ipLocation';

export default {
  name: 'ArtistVideoSection',
  data() {
    return {
      videoId: 'iJ5UWgVDfWk',
      isIndonesiaIp: false,
      shouldLoadVideo: false,
      showLoader: true,
      videoReady: false,
      thumbnailFailed: false,
      observer: null,
    }
  },
  computed: {
    isVisible() {
      // Show if user is in Indonesia OR using Indonesian language
      return this.isIndonesiaIp || (this.$i18n && this.$i18n.locale === 'ID');
    },
    iframeSrc() {
      return `https://www.youtube.com/embed/${this.videoId}?autoplay=1&mute=1&loop=1&playlist=${this.videoId}&controls=1&rel=0&modestbranding=1&playsinline=1&enablejsapi=1`;
    },
    thumbnailUrl() {
      return `https://img.youtube.com/vi/${this.videoId}/maxresdefault.jpg`;
    },
    thumbnailUrlFallback() {
      return `https://img.youtube.com/vi/${this.videoId}/hqdefault.jpg`;
    },
    currentThumbnailUrl() {
      return this.thumbnailFailed ? this.thumbnailUrlFallback : this.thumbnailUrl;
    }
  },
  watch: {
    isVisible(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.setupIntersectionObserver();
        });
      }
    },
  },
  async mounted() {
    await this.checkLocation();
    if (this.isVisible) {
      this.$nextTick(() => {
        this.setupIntersectionObserver();
      });
    }
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  methods: {
    setupIntersectionObserver() {
      if (this.observer || this.shouldLoadVideo) return;
      const card = this.$refs.videoCard;
      if (!card) return;

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.shouldLoadVideo = true;
            this.observer.disconnect();
            this.observer = null;
          }
        });
      }, { threshold: 0.1 });

      this.observer.observe(card);
    },
    async checkLocation() {
      try {
        const data = await getIpLocation();
        if (data && (data.countryCode === 'ID' || data.country === 'Indonesia')) {
          this.isIndonesiaIp = true;
        }
      } catch (error) {
        console.error('Failed to detect location:', error);
      }
    },
    onThumbnailError() {
      // If maxresdefault fails, fall back to hqdefault
      this.thumbnailFailed = true;
    },
    onIframeLoad() {
      // The iframe has loaded — give YouTube player a moment to render
      // its first frame, then reveal it and dismiss the overlay
      setTimeout(() => {
        this.videoReady = true;
        this.showLoader = false;
      }, 1200);
    }
  }
}
</script>

<style scoped>
.artist-video-section {
  padding: 2.5rem 0;  
  background-color: #022534;
  display: flex;
  justify-content: center;
}

.video-card {
  position: relative;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  border-radius: 20px;
  overflow: hidden;
  box-shadow:
    0 4px 16px rgba(0, 0, 0, 0.3),
    0 1px 4px rgba(0, 0, 0, 0.2);
  /* 16:9 aspect ratio */
  padding-bottom: 56.25%;
  height: 0;
  background: #000;
}

.video-thumbnail {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 1;
}

.video-card iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
  z-index: 2;
  opacity: 0;
  transition: opacity 0.5s ease;
  background: transparent;
}

.video-card iframe.iframe-visible {
  opacity: 1;
}

/* ── Loading overlay ── */
.video-loader {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.25);
  overflow: hidden;
}

/* Shimmer sweep */
.loader-shimmer {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.04) 40%,
    rgba(255, 255, 255, 0.08) 50%,
    rgba(255, 255, 255, 0.04) 60%,
    transparent 100%
  );
  animation: shimmer 2s infinite ease-in-out;
}

@keyframes shimmer {
  0%   { left: -100%; }
  100% { left: 100%; }
}

/* Centered content (ring + play icon) */
.loader-content {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
}

.loader-pulse-ring {
  position: absolute;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.15);
  animation: pulse-ring 1.8s infinite ease-out;
}

@keyframes pulse-ring {
  0%   { transform: scale(0.85); opacity: 1; }
  70%  { transform: scale(1.35); opacity: 0; }
  100% { transform: scale(1.35); opacity: 0; }
}

.loader-play-icon {
  width: 68px;
  height: 68px;
  animation: gentle-pulse 2s infinite ease-in-out;
}

@keyframes gentle-pulse {
  0%, 100% { transform: scale(1); opacity: 0.7; }
  50%      { transform: scale(1.06); opacity: 1; }
}

/* Fade-out transition */
.fade-loader-leave-active {
  transition: opacity 0.5s ease;
}
.fade-loader-leave-to {
  opacity: 0;
}

@media (max-width: 600px) {
  .video-card {
    border-radius: 12px;
    margin: 0 1rem;
    /* Recalculate aspect ratio for smaller margin */
    max-width: calc(100% - 2rem);
  }
  .artist-video-section {
    padding: 1.5rem 0;
  }
}
</style>
